import React, { useMemo } from "react";
import Helmet, { HelmetProps } from "react-helmet";
import cn from "clsx";
import { isFunction } from "../../utils/helpers";
import useT from "../../hooks/useT";
import "./Page.css";

export interface PageProps {
  /** The page's language. */
  lang?: string;
  /** The page title. */
  title?: string;
  /**
   * The page's name, used for generating the class `page-name-${name}`
   * which is applied to the element and to the body element.
   */
  name?: string;
  /** The class name(s) to attach to the page. */
  className?: string;
  /** Extra content to put into the header. */
  headerContent?: React.ReactNode | React.ReactNodeArray;
  /** Extra content to put into the footer. */
  footerContent?: React.ReactNode | React.ReactNodeArray;
  /** The page content. */
  children: React.ReactNode | React.ReactNodeArray;
}

export default function Page({
  lang,
  name,
  className,
  title,
  headerContent,
  footerContent,
  children
}: PageProps) {
  const { i18n } = useT();
  const lng = lang || i18n.language;

  const helmetProps: HelmetProps = useMemo(() => {
    return {
      title,
      htmlAttributes: {
        lang: lng,
        dir: isFunction(i18n.dir) ? i18n.dir(lng) : null
      },
      bodyAttributes: {
        class: cn(!!name && `page-name-${name}`)
      }
    };
  }, [name, lng]);

  return (
    <article
      className={cn("page", !!name && `page-name-${name}`, className)}
      lang={helmetProps.htmlAttributes.lang}
    >
      <Helmet {...helmetProps} />
      {(!!title || !!headerContent) && (
        <header className="page-header">
          {!!title && <h2 className="page-title">{title}</h2>}
          {!!headerContent ? headerContent : null}
        </header>
      )}
      <div className="page-content">{children}</div>
      {!!footerContent && (
        <footer className="page-footer">{footerContent}</footer>
      )}
    </article>
  );
}
